/**
 * Google Maps API를 동적으로 로드하는 유틸리티
 */

interface GoogleMapsLoadStatus {
  isLoaded: boolean;
  isLoading: boolean;
  loadPromise: Promise<boolean> | null;
}

// Google Maps SDK 로드 상태를 추적하는 객체
const googleMapsLoadStatus: GoogleMapsLoadStatus = {
  isLoaded: false,
  isLoading: false,
  loadPromise: null
};

/**
 * Google Maps SDK가 로드되었는지 확인하는 함수
 */
export const isGoogleMapsLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).google !== 'undefined' && 
    (window as any).google.maps !== undefined && 
    (window as any).google.maps.Map !== undefined
  );
};

/**
 * Google Maps API를 동적으로 로드하는 함수
 */
export const loadGoogleMapsScript = (): Promise<boolean> => {
  // 이미 로드되었으면 즉시 완료
  if (isGoogleMapsLoaded()) {
    googleMapsLoadStatus.isLoaded = true;
    return Promise.resolve(true);
  }

  // 이미 로딩 중이면 기존 프로미스 반환
  if (googleMapsLoadStatus.isLoading && googleMapsLoadStatus.loadPromise) {
    return googleMapsLoadStatus.loadPromise;
  }

  // 환경변수에서 API 키 가져오기
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.error('Google Maps API key not found in environment variables');
    return Promise.resolve(false);
  }

  // 로딩 상태로 변경
  googleMapsLoadStatus.isLoading = true;
  
  // Google Maps 스크립트를 동적으로 로드
  googleMapsLoadStatus.loadPromise = new Promise<boolean>((resolve, reject) => {
    try {
      // 글로벌 콜백 함수 설정
      (window as any).googleMapLoadCallback = () => {
        googleMapsLoadStatus.isLoaded = true;
        googleMapsLoadStatus.isLoading = false;
        (window as any).googleMapLoaded = true;
        resolve(true);
      };

      // 에러 콜백 함수 설정
      (window as any).googleMapErrorCallback = (error: any) => {
        console.error('Google Maps API 로드 오류:', error);
        googleMapsLoadStatus.isLoading = false;
        googleMapsLoadStatus.loadPromise = null;
        reject(new Error('Google Maps API loading failed'));
      };

      // 스크립트 엘리먼트 생성
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry&callback=googleMapLoadCallback`;
      
      script.onerror = (window as any).googleMapErrorCallback;
      
      // 타임아웃 설정 (10초)
      const timeout = setTimeout(() => {
        console.error('Google Maps API loading timed out');
        googleMapsLoadStatus.isLoading = false;
        googleMapsLoadStatus.loadPromise = null;
        reject(new Error('Google Maps API loading timed out'));
      }, 10000);

      // 로드 성공 시 타임아웃 클리어
      const originalCallback = (window as any).googleMapLoadCallback;
      (window as any).googleMapLoadCallback = () => {
        clearTimeout(timeout);
        originalCallback();
      };

      // 스크립트를 DOM에 추가
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }

    } catch (error) {
      console.error('Error creating Google Maps script:', error);
      googleMapsLoadStatus.isLoading = false;
      googleMapsLoadStatus.loadPromise = null;
      reject(error);
    }
  });

  return googleMapsLoadStatus.loadPromise;
};

/**
 * Google Maps SDK 초기화 함수
 * 컴포넌트에서 사용 전에 호출해야 합니다.
 */
export const initGoogleMaps = async (): Promise<boolean> => {
  try {
    // 이미 로드되었으면 즉시 반환
    if (isGoogleMapsLoaded()) {
      return true;
    }
    
    const result = await loadGoogleMapsScript();
    return result;
  } catch (error) {
    console.error('Failed to initialize Google Maps:', error);
    return false;
  }
}; 